/**
 * product-form-atc.js
 *
 * Responsibilities:
 *  - Intercepts product form submission (PDP)
 *  - Handles .cw-hp-product-atc-btn clicks (collection / home page)
 *  - Renders an optimistic cart item immediately in the drawer
 *  - Opens the cart drawer after ATC
 *  - Suppresses the Rebuy cart flyout
 *  - Handles drawer open / close triggers
 *  - Adds free gifts when ATC is triggered from any context
 *
 * Config is read from <script id="pf-config" type="application/json"> injected by
 * the product-form.liquid snippet so this file stays fully static / CDN-cacheable.
 */

// Kill the legacy CWCollectionATCBridgeV4 in cart-drawer.js before it can
// execute — this must run at parse time (top of file), not inside the IIFE,
// because both files load with defer and execution order is not guaranteed.
window.CWCollectionATCBridgeV4 = true;

if (window.CWProductFormATCInitialized) {
  console.warn('[ATC] product-form-atc.js already initialized. Skipping duplicate include.');
} else {
  window.CWProductFormATCInitialized = true;

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     0.  Config
  ───────────────────────────────────────────────────────────── */
  const cfg = (function () {
    try {
      return JSON.parse(document.getElementById('pf-config')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  })();

  const PRODUCT_DATA     = cfg.product || {};
  const MONEY_FORMAT     = cfg.moneyFormat || '${{amount}}';
  const OPTIMISTIC_DELAY = 800;
  const GIFT_MAX_QTY     = 2;

  function getGiftAddQuantity(quantity) {
    const qty = Math.max(1, Number(quantity || 1));
    return Math.min(GIFT_MAX_QTY, qty);
  }

  function rememberGiftRule(rule) {
    if (!rule || !Array.isArray(rule.triggerIds) || !Array.isArray(rule.gifts) || !rule.gifts.length) return;

    window.CWGiftRules = Array.isArray(window.CWGiftRules) ? window.CWGiftRules : [];

    const signature = JSON.stringify({
      triggerIds: (rule.triggerIds || []).map(Number).filter(Boolean).sort(),
      gifts: (rule.gifts || [])
        .map((gift) => Number(gift.variantId || gift.variant_id || gift.id || 0))
        .filter(Boolean)
        .sort(),
    });

    const exists = window.CWGiftRules.some((existingRule) => {
      const existingSignature = JSON.stringify({
        triggerIds: (existingRule.triggerIds || []).map(Number).filter(Boolean).sort(),
        gifts: (existingRule.gifts || [])
          .map((gift) => Number(gift.variantId || gift.variant_id || gift.id || 0))
          .filter(Boolean)
          .sort(),
      });

      return existingSignature === signature;
    });

    if (!exists) window.CWGiftRules.push(rule);
  }

  /* ─────────────────────────────────────────────────────────────
     0b. Dynamic gift rule registration
  ───────────────────────────────────────────────────────────── */
  (function registerPageGiftRule() {
    const rule = cfg.giftRule;
    if (!rule || !rule.triggerIds || !rule.gifts || !rule.gifts.length) return;

    window._pendingGiftRule = rule;
    rememberGiftRule(rule);

    console.log(
      '[ATC] Queued page gift rule for product:',
      PRODUCT_DATA.id,
      '→',
      rule.gifts.map((g) => g.title).join(', ')
    );

    if (window.GiftSync?.registerRule) {
      window.GiftSync.registerRule(rule);
    }
  })();

  function registerDynamicGiftRule(rule) {
    if (!rule || !Array.isArray(rule.triggerIds) || !Array.isArray(rule.gifts) || !rule.gifts.length) return;

    rememberGiftRule(rule);

    if (window.GiftSync?.registerRule) {
      window.GiftSync.registerRule(rule);
      return;
    }

    window._pendingGiftRule = rule;
  }

  function getVariantGiftRule(selectedVariantId) {
    const variantGiftRules = cfg.variantGiftRules || {};
    return variantGiftRules[String(selectedVariantId)] || null;
  }

  const SCOPED_CONFIG_CACHE = new WeakMap();

  function parseScopedConfigScript(script) {
    if (!script) return null;

    if (SCOPED_CONFIG_CACHE.has(script)) {
      return SCOPED_CONFIG_CACHE.get(script);
    }

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      SCOPED_CONFIG_CACHE.set(script, parsed);
      return parsed;
    } catch (_) {
      SCOPED_CONFIG_CACHE.set(script, null);
      return null;
    }
  }

  function getScopedConfig(source) {
    if (!source || source === document) return cfg;

    const root = getCollectionRoot(source);
    const localScript =
      root?.querySelector?.('script#pf-config, script[data-pf-config], script[type="application/json"][id^="pf-config"]') ||
      source?.closest?.('.grid-product__content, .grid-product, .collection-grid-item, .cw-hp-product, .product-card, .card-wrapper')
        ?.querySelector?.('script#pf-config, script[data-pf-config], script[type="application/json"][id^="pf-config"]');

    return parseScopedConfigScript(localScript) || cfg;
  }

  function getScopedVariantGiftRule(source, selectedVariantId) {
    const scopedCfg = getScopedConfig(source);
    const variantGiftRules = scopedCfg.variantGiftRules || cfg.variantGiftRules || {};
    return variantGiftRules[String(selectedVariantId)] || null;
  }

  function buildRuntimeGiftRule(source, variantId, productId, gifts) {
    const vid = Number(variantId || 0);
    const pid = Number(productId || 0);
    const resolvedGifts = (gifts || []).filter((gift) => Number(gift.variantId || gift.variant_id || gift.id || 0));

    if (!resolvedGifts.length) return null;

    const scopedCfg = getScopedConfig(source);
    const candidates = [
      scopedCfg.giftRule,
      getScopedVariantGiftRule(source, vid),
    ].filter(Boolean);

    const matchingRule = candidates.find((rule) => {
      const triggerIds = (rule.triggerIds || []).map(Number);
      return triggerIds.includes(pid) || triggerIds.includes(vid);
    });

    if (matchingRule) return matchingRule;

    return {
      triggerIds: [pid, vid].filter(Boolean),
      gifts: resolvedGifts,
      _source: 'collection-card',
    };
  }

  /* ─────────────────────────────────────────────────────────────
     1.  Tiny DOM helpers
  ───────────────────────────────────────────────────────────── */
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function esc(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  /* ─────────────────────────────────────────────────────────────
     2.  Money formatting
  ───────────────────────────────────────────────────────────── */
  function formatMoneyRaw(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, MONEY_FORMAT);
    }

    if (window.theme?.Currency?.formatMoney) {
      return window.theme.Currency.formatMoney(cents, MONEY_FORMAT);
    }

    return '$' + (Number(cents || 0) / 100).toFixed(2) + ' USD';
  }

  function moneyHTML(cents) {
    const value = formatMoneyRaw(cents);
    return String(value).includes('<') ? value : `<span class="money">${esc(value)}</span>`;
  }

  function setMoneyHTML(el, cents) {
    if (el) el.innerHTML = moneyHTML(cents);
  }

  /* ─────────────────────────────────────────────────────────────
     3.  Rebuy suppression
  ───────────────────────────────────────────────────────────── */
  function injectRebuySuppressCSS() {
    if (document.getElementById('cw-rebuy-suppress-css')) return;

    const style = document.createElement('style');
    style.id = 'cw-rebuy-suppress-css';
    style.textContent = `
      html.cw-rebuy-cart-suppressed .rebuy-cart,
      html.cw-rebuy-cart-suppressed .rebuy-cart__flyout,
      html.cw-rebuy-cart-suppressed .rebuy-cart__background,
      html.cw-rebuy-cart-suppressed .rebuy-cart__overlay,
      html.cw-rebuy-cart-suppressed .rebuy-cart__drawer,
      html.cw-rebuy-cart-suppressed .rebuy-cart__popup,
      html.cw-rebuy-cart-suppressed .rebuy-cart__modal,
      html.cw-rebuy-cart-suppressed #rebuy-cart {
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      html.cw-rebuy-cart-suppressed .rebuy-cart,
      html.cw-rebuy-cart-suppressed .rebuy-cart__flyout,
      html.cw-rebuy-cart-suppressed .rebuy-cart__drawer,
      html.cw-rebuy-cart-suppressed #rebuy-cart {
        transform: translateX(110%) !important;
      }
    `;
    document.head.appendChild(style);
  }

  function suppressRebuyCart(duration) {
    injectRebuySuppressCSS();
    document.documentElement.classList.add('cw-rebuy-cart-suppressed');

    const sel = [
      '.rebuy-cart',
      '.rebuy-cart__flyout',
      '.rebuy-cart__background',
      '.rebuy-cart__overlay',
      '.rebuy-cart__drawer',
      '.rebuy-cart__popup',
      '.rebuy-cart__modal',
      '#rebuy-cart',
    ].join(',');

    const SLIDE_TARGETS = new Set([
      '.rebuy-cart',
      '.rebuy-cart__flyout',
      '.rebuy-cart__drawer',
      '#rebuy-cart',
    ]);

    function hide() {
      qsa(sel).forEach((el) => {
        el.classList.remove('is-open', 'active', 'open');
        el.setAttribute('aria-hidden', 'true');
        Object.assign(el.style, {
          opacity: '0',
          visibility: 'hidden',
          pointerEvents: 'none',
        });

        if (SLIDE_TARGETS.has('.' + (el.className.split(' ')[0] || '')) || el.id === 'rebuy-cart') {
          el.style.transform = 'translateX(110%)';
        }
      });
    }

    hide();

    const started = Date.now();
    const timer = setInterval(() => {
      hide();
      if (Date.now() - started > (duration || 3500)) clearInterval(timer);
    }, 80);
  }

  /* ─────────────────────────────────────────────────────────────
     4.  Drawer open / close
  ───────────────────────────────────────────────────────────── */
  let drawerOpenTimer = null;

  function openDrawerNow() {
    clearTimeout(drawerOpenTimer);

    drawerOpenTimer = setTimeout(() => {
      const drawer = document.getElementById('CartDrawer');
      const overlay = document.getElementById('cd-overlay');

      if (!drawer) return;

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');

      if (overlay) {
        overlay.classList.add('is-open');
        overlay.style.display = '';
      }

      document.documentElement.classList.add('js-drawer-open', 'js-drawer-open--right');
      document.body.style.overflow = 'hidden';
    }, 800);
  }

  function closeDrawerNow() {
    clearTimeout(drawerOpenTimer);
    drawerOpenTimer = null;

    const drawer = document.getElementById('CartDrawer');
    const overlay = document.getElementById('cd-overlay');

    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }

    if (overlay) {
      overlay.classList.remove('is-open');
      overlay.style.display = 'none';
    }

    document.documentElement.classList.remove('js-drawer-open', 'js-drawer-open--right');
    document.body.style.overflow = '';
  }

  /* ─────────────────────────────────────────────────────────────
     5.  Drawer open / close event wiring
  ───────────────────────────────────────────────────────────── */
  document.addEventListener(
    'click',
    (e) => {
      if (!e.target.closest('.js-drawer-open-cart, [aria-controls="CartDrawer"]')) return;

      e.preventDefault();
      e.stopPropagation();

      suppressRebuyCart(4500);
      openDrawerNow();
    },
    true
  );

  document.addEventListener(
    'click',
    (e) => {
      if (!e.target.closest('#cd-overlay, #cd-btn-close, .cd-close, .js-drawer-close')) return;

      e.preventDefault();
      e.stopPropagation();

      closeDrawerNow();
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawerNow();
  });

  /* ─────────────────────────────────────────────────────────────
     6.  Variant resolution helpers
  ───────────────────────────────────────────────────────────── */
  function getVariantById(id) {
    if (!id || !PRODUCT_DATA.variants) return null;
    return PRODUCT_DATA.variants.find((v) => String(v.id) === String(id)) || null;
  }

  function getSwatcherVariantId() {
    const swatcherVariantId = window.__ks_variantId;

    if (swatcherVariantId && getVariantById(swatcherVariantId)) {
      return String(swatcherVariantId);
    }

    const activeSwatch = document.querySelector('.cw-colors-swatchers__item.is-active[data-variant-id]');
    const activeSwatchVariantId = activeSwatch?.getAttribute('data-variant-id');

    if (activeSwatchVariantId && getVariantById(activeSwatchVariantId)) {
      return String(activeSwatchVariantId);
    }

    return '';
  }

  function forceFormVariantFromSwatcher(form) {
    const variantId = getSwatcherVariantId();
    if (!variantId) return '';

    const idInput = form.querySelector('[name="id"]');

    if (idInput) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      idInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    return variantId;
  }

  function getVariantFromSelectedOptions(form) {
    const opts = [];

    for (let i = 1; i <= 3; i++) {
      const checked = qs(`[name="option${i}"]:checked, [name="options[${i}]"]:checked`, form);
      const select = qs(`select[name="option${i}"], select[name="options[${i}]"]`, form);

      if (checked) opts.push(checked.value);
      else if (select) opts.push(select.value);
    }

    qsa('[name^="options["]', form).forEach((input) => {
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      if (input.value && !opts.includes(input.value)) opts.push(input.value);
    });

    if (!opts.length) return null;

    return (
      PRODUCT_DATA.variants?.find((v) =>
        opts.every((val, i) => String(v.options[i]) === String(val))
      ) || null
    );
  }

  function getSelectedVariant(form) {
    const formData = new FormData(form);
    const swatcherVariantId = getSwatcherVariantId();

    return (
      getVariantById(swatcherVariantId) ||
      getVariantById(formData.get('id')) ||
      getVariantById(qs('[name="id"]', form)?.value) ||
      getVariantById(qs('[data-variant-id]', form)?.getAttribute('data-variant-id')) ||
      getVariantById(
        qs(
          '[data-variant-id].is-selected,[data-variant-id][aria-checked="true"],' +
            '[data-variant-id][aria-selected="true"],[data-variant-id][checked]',
          form
        )?.getAttribute('data-variant-id')
      ) ||
      getVariantFromSelectedOptions(form) ||
      getVariantById(new URLSearchParams(window.location.search).get('variant')) ||
      PRODUCT_DATA.variants?.[0]
    );
  }

  function getCartDrawerConfig() {
    try {
      return JSON.parse(document.getElementById('cd-config')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  function getLineItemPropertiesFromForm(formData) {
    const properties = {};

    formData.forEach((value, key) => {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (!match || value === '') return;
      properties[match[1]] = value;
    });

    return properties;
  }

  /**
   * Resolves gift items for any variantId / productId combination.
   * Works both in PDP context (PRODUCT_DATA populated) and in collection/home
   * context (where PRODUCT_DATA may be empty — relies solely on cd-config giftRules).
   */
  function getGiftsForVariant(variantId, productId, source) {
    const gifts = new Map();

    const vid = Number(variantId || 0);
    const pid = Number(productId || PRODUCT_DATA.id || 0);

    const cdConfig = getCartDrawerConfig();
    const scopedCfg = source ? getScopedConfig(source) : cfg;
    const hardcodedRules = Array.isArray(cdConfig.giftRules) ? cdConfig.giftRules : [];
    const dynamicRule = scopedCfg.giftRule || cfg.giftRule;
    const scopedVariantRules = scopedCfg.variantGiftRules || cfg.variantGiftRules || {};
    const variantRule = scopedVariantRules[String(vid)] || null;

    const allRules = [
      ...hardcodedRules,
      ...(dynamicRule ? [dynamicRule] : []),
      ...(variantRule ? [variantRule] : []),
    ];

    allRules.forEach((rule) => {
      const triggerIds = (rule.triggerIds || []).map(Number);

      if (!triggerIds.includes(pid) && !triggerIds.includes(vid)) return;

      (rule.gifts || []).forEach((gift) => {
        const giftVariantId = Number(gift.variantId || gift.variant_id || gift.id || 0);

        if (!giftVariantId || giftVariantId === vid || gifts.has(giftVariantId)) return;

        gifts.set(giftVariantId, gift);
      });
    });

    return Array.from(gifts.values());
  }

  // Legacy alias used by PDP context — delegates to the unified resolver
  function getGiftsForCurrentProduct(selectedVariantId) {
    return getGiftsForVariant(selectedVariantId, PRODUCT_DATA.id);
  }

  function buildCartAddPayloadFromForm(form) {
    const forcedVariantId = forceFormVariantFromSwatcher(form);
    const formData = new FormData(form);
    const selectedVariant = getSelectedVariant(form);

    const mainVariantId = Number(forcedVariantId || formData.get('id') || selectedVariant?.id || 0);
    const quantity = Math.max(1, Number(formData.get('quantity') || 1));
    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    const properties = getLineItemPropertiesFromForm(formData);

    if (!mainVariantId) {
      throw new Error('Missing selected variant ID.');
    }

    const mainItem = {
      id: mainVariantId,
      quantity,
    };

    if (sellingPlanId) {
      mainItem.selling_plan = sellingPlanId;
    }

    if (Object.keys(properties).length) {
      mainItem.properties = properties;
    }

    const giftQuantity = getGiftAddQuantity(quantity);
    const giftItems = getGiftsForCurrentProduct(mainVariantId)
      .map((gift) => ({
        id: Number(gift.variantId || gift.variant_id || gift.id || 0),
        quantity: giftQuantity,
        properties: {
          _gift: 'true',
          _parent_product_id: String(PRODUCT_DATA.id),
          _parent_variant_id: String(mainVariantId),
        },
      }))
      .filter((item) => item.id && item.id !== mainVariantId);

    return {
      items: [mainItem, ...giftItems],
    };
  }

  function getSellingPlanText(form, variant) {
    const spId = qs('[name="selling_plan"]', form)?.value || '';

    if (spId && PRODUCT_DATA.selling_plan_groups?.length) {
      for (const group of PRODUCT_DATA.selling_plan_groups) {
        for (const plan of group.selling_plans || []) {
          if (String(plan.id) === String(spId)) {
            return plan.options?.[0]?.value || plan.name || '';
          }
        }
      }
    }

    if (variant?.requires_selling_plan && PRODUCT_DATA.selling_plan_groups?.length) {
      const group = PRODUCT_DATA.selling_plan_groups[0];

      return (
        group.options?.[0]?.values?.[0] ||
        group.selling_plans?.[0]?.options?.[0]?.value ||
        group.selling_plans?.[0]?.name ||
        ''
      );
    }

    return '';
  }

  function getVariantImage(variant) {
    return variant?.featured_image?.src || PRODUCT_DATA.featured_image || PRODUCT_DATA.images?.[0] || '';
  }

  /* ─────────────────────────────────────────────────────────────
     7.  Optimistic drawer updates
  ───────────────────────────────────────────────────────────── */
  function hideEmptyState() {
    const el = document.getElementById('cd-empty');
    if (el) el.style.display = 'none';
  }

  function getCurrentSubtotalCents() {
    const el = qs('[data-subtotal-price]');
    if (!el) return 0;

    const raw = el.textContent.replace(/[^\d.,]/g, '').trim();
    if (!raw) return 0;

    const norm =
      raw.includes(',') && raw.lastIndexOf(',') > raw.lastIndexOf('.')
        ? raw.replace(/\./g, '').replace(',', '.')
        : raw.replace(/,/g, '');

    return Math.round(parseFloat(norm || '0') * 100);
  }

  function updateOptimisticSubtotal(variant, qty) {
    const subtotalEl = qs('[data-subtotal-price]');
    const labelEl = qs('[data-subtotal-label]');

    if (!subtotalEl || !variant) return;

    setMoneyHTML(subtotalEl, getCurrentSubtotalCents() + Number(variant.price || 0) * qty);

    if (labelEl) {
      const match = labelEl.textContent.match(/\((\d+)/);
      const cur = match ? Number(match[1]) : document.querySelectorAll('#cd-items .cd-item').length;
      const next = cur + qty;

      labelEl.textContent = `Subtotal (${next} item${next === 1 ? '' : 's'})`;
    }
  }

  function updateOptimisticProgress(form) {
    const fill = qs('[data-progress-fill]');
    const label = qs('[data-progress-label]');

    if (!qs('[name="selling_plan"]', form)?.value || !fill || !label) return;

    fill.style.width = '100%';
    fill.classList.add('is-full');
    label.innerHTML = "🎉 Congrats! You've unlocked <strong>FREE SHIPPING!</strong>";
  }



  function updateExistingItemOptimistic(li, variant, qty) {
    const qtyEl = qs('.cd-qty-val', li);
    const priceBlock = qs('.cd-price-block', li);
    const currentQty = qtyEl ? Number(qtyEl.textContent.trim() || 1) : 1;
    const nextQty = currentQty + qty;
    const linePrice = Number(variant.price || 0) * nextQty;
    const cmpPrice = Number(variant.compare_at_price || 0) * nextQty;

    li.classList.add('is-optimistic');

    if (qtyEl) qtyEl.textContent = nextQty;

    if (priceBlock) {
      priceBlock.innerHTML =
        cmpPrice > linePrice
          ? `<span class="cd-p cd-p-sale">${moneyHTML(linePrice)}</span><span class="cd-p cd-p-was">${moneyHTML(cmpPrice)}</span>`
          : `<span class="cd-p">${moneyHTML(linePrice)}</span>`;
    }
  }

  function buildOptimisticItemLi(variant, qty, form, productTitle, productUrl) {
    const image = getVariantImage(variant);
    const title = productTitle || PRODUCT_DATA.title || '';
    const url = productUrl
      ? `${productUrl}?variant=${esc(variant.id)}`
      : `${esc(PRODUCT_DATA.url || window.location.pathname)}?variant=${esc(variant.id)}`;
    const varTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    const subText = form ? getSellingPlanText(form, variant) : '';
    const metaLines = [varTitle, subText].filter(Boolean);
    const linePrice = Number(variant.price || 0) * qty;
    const cmpPrice = Number(variant.compare_at_price || 0) * qty;

    const li = document.createElement('li');
    li.className = 'cd-item is-optimistic';
    li.dataset.key = `optimistic-${Date.now()}`;
    li.dataset.variant = variant.id;
    li.dataset.optimisticItem = 'true';

    li.innerHTML = `
      <a href="${url}" class="cd-item-thumb" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${esc(image)}" alt="${esc(title)}" width="80" height="80" loading="eager">` : ''}
      </a>
      <div class="cd-item-info">
        <a href="${url}" class="cd-item-name">${esc(title)}</a>
        ${metaLines.length ? `<p class="cd-item-meta">${esc(metaLines.join(' • '))}</p>` : ''}
        <div class="cd-item-foot">
          <div class="cd-qty-widget">
            <button type="button" class="cd-qbtn" disabled aria-label="Decrease">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                <path d="M1 1H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="cd-qty-val">${qty}</span>
            <button type="button" class="cd-qbtn" disabled aria-label="Increase">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="cd-price-block">
            ${
              cmpPrice > linePrice
                ? `<span class="cd-p cd-p-sale">${moneyHTML(linePrice)}</span><span class="cd-p cd-p-was">${moneyHTML(cmpPrice)}</span>`
                : `<span class="cd-p">${moneyHTML(linePrice)}</span>`
            }
          </div>
        </div>
      </div>
      <button type="button" class="cd-del" disabled aria-label="Remove">
        <svg width="13" height="15" viewBox="0 0 13 15" fill="none" aria-hidden="true">
          <path d="M1 3.5H12M4.5 3.5V2H8.5V3.5M2 3.5L2.6 13H10.4L11 3.5"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    return li;
  }

  function renderOptimisticItem(form) {
    const list = document.getElementById('cd-items');
    if (!list) return;

    forceFormVariantFromSwatcher(form);

    const variant = getSelectedVariant(form);
    if (!variant) return;

    const qty = Math.max(1, Number(qs('[name="quantity"]', form)?.value || 1));

    if (window.GiftSync?.isGift?.({ variant_id: variant.id, product_id: PRODUCT_DATA.id })) return;

    const existingReal = qs(`.cd-item[data-variant="${variant.id}"]:not([data-optimistic-item])`, list);

    hideEmptyState();

    const footerEl = document.querySelector('#CartDrawer .cd-footer');
    if (footerEl) footerEl.style.display = '';

    if (existingReal) {
      updateExistingItemOptimistic(existingReal, variant, qty);
    } else {
      qs(`[data-optimistic-item="true"][data-variant="${variant.id}"]`, list)?.remove();
      list.prepend(buildOptimisticItemLi(variant, qty, form));
    }

    updateOptimisticSubtotal(variant, qty);
    updateOptimisticProgress(form);
  }

  const giftMetaCache = new Map();

  function normalizeImageUrl(value) {
    if (!value) return '';

    let url = '';

    if (typeof value === 'string') {
      url = value;
    } else if (typeof value === 'object') {
      url =
        value.src ||
        value.url ||
        value.preview_image?.src ||
        value.preview_image?.url ||
        value.featured_image?.src ||
        value.featured_image?.url ||
        '';
    }

    if (!url) return '';

    if (url.startsWith('//')) return `https:${url}`;

    return url;
  }

  function normalizeProductJsonUrl(rawUrl) {
    if (!rawUrl || rawUrl === '#') return '';

    let url = String(rawUrl).split('?')[0];

    if (!url) return '';
    if (url.endsWith('.js')) return url;

    return `${url}.js`;
  }

  function getCachedCartGiftItem(variantId) {
    const cachedCart = window._giftSyncCartCache;

    return (
      cachedCart?.items?.find((item) => Number(item.variant_id) === Number(variantId)) ||
      null
    );
  }

  function getGiftImage(gift, hydrated) {
    return (
      normalizeImageUrl(gift.image) ||
      normalizeImageUrl(gift.imageUrl) ||
      normalizeImageUrl(gift.image_url) ||
      normalizeImageUrl(gift.featured_image) ||
      normalizeImageUrl(gift.featuredImage) ||
      normalizeImageUrl(gift.variant?.featured_image) ||
      normalizeImageUrl(hydrated?.image) ||
      normalizeImageUrl(hydrated?.featured_image) ||
      normalizeImageUrl(hydrated?.featuredImage) ||
      ''
    );
  }

  function getGiftTitle(gift, hydrated) {
    const title =
      gift.title ||
      gift.product_title ||
      gift.name ||
      hydrated?.product_title ||
      hydrated?.title ||
      'Free Gift';

    return String(title).replace(/\s+-\s+Default Title$/i, '');
  }

  function getGiftUrl(gift, hydrated, variantId) {
    const rawUrl = gift.url || hydrated?.url || '#';

    if (rawUrl === '#') return rawUrl;
    if (rawUrl.includes('variant=')) return rawUrl;

    return `${rawUrl}${rawUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
  }

  function getGiftCompareAtPrice(gift, hydrated) {
    return Number(
      gift.compareAtPrice ||
      gift.compare_at_price ||
      gift.original_price ||
      gift.price ||
      hydrated?.compareAtPrice ||
      hydrated?.compare_at_price ||
      hydrated?.original_price ||
      hydrated?.price ||
      0
    );
  }

  async function hydrateGiftMeta(gift) {
    const variantId = Number(gift.variantId || gift.variant_id || gift.id || 0);
    if (!variantId) return gift;

    if (giftMetaCache.has(variantId)) {
      return {
        ...gift,
        ...giftMetaCache.get(variantId),
      };
    }

    let hydrated = {};
    const cachedCartItem = getCachedCartGiftItem(variantId);

    if (cachedCartItem) {
      hydrated = {
        title: cachedCartItem.product_title || cachedCartItem.title,
        product_title: cachedCartItem.product_title || cachedCartItem.title,
        url: cachedCartItem.url,
        image:
          cachedCartItem.image ||
          cachedCartItem.featured_image?.url ||
          cachedCartItem.featured_image,
        price: Number(cachedCartItem.price || cachedCartItem.original_price || 0),
        compare_at_price: Number(cachedCartItem.original_price || cachedCartItem.price || 0),
      };
    }

    try {
      const productJsonUrl =
        normalizeProductJsonUrl(gift.url || hydrated.url) ||
        (gift.handle ? `/products/${gift.handle}.js` : '');

      if (productJsonUrl && (!hydrated.image || !hydrated.title)) {
        const productRes = await fetch(productJsonUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (productRes.ok) {
          const product = await productRes.json();
          const variant = (product.variants || []).find((v) => Number(v.id) === variantId);

          hydrated = {
            ...hydrated,
            title: hydrated.title || product.title,
            product_title: hydrated.product_title || product.title,
            url: hydrated.url || product.url || String(productJsonUrl).replace(/\.js$/, ''),
            image: normalizeImageUrl(
              hydrated.image ||
                variant?.featured_image ||
                product.featured_image ||
                product.images?.[0] ||
                gift.image ||
                gift.featured_image ||
                gift.featuredImage
            ),
            price: Number(hydrated.price || variant?.price || 0),
            compare_at_price: Number(
              hydrated.compare_at_price ||
                variant?.compare_at_price ||
                variant?.price ||
                0
            ),
          };
        }
      }

      if (!hydrated.title || !hydrated.image || !hydrated.compare_at_price) {
        const variantRes = await fetch(`/variants/${variantId}.js`, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (variantRes.ok) {
          const variant = await variantRes.json();

          hydrated = {
            ...hydrated,
            title:
              hydrated.title ||
              gift.title ||
              gift.product_title ||
              variant.name ||
              variant.title,
            image: normalizeImageUrl(
              hydrated.image ||
                variant.featured_image ||
                gift.image ||
                gift.featured_image ||
                gift.featuredImage
            ),
            price: Number(hydrated.price || variant.price || 0),
            compare_at_price: Number(
              hydrated.compare_at_price ||
                variant.compare_at_price ||
                variant.price ||
                0
            ),
          };
        }
      }
    } catch (error) {
      console.warn('[ATC] Gift metadata hydration failed:', variantId, error);
    }

    const normalized = {
      title: getGiftTitle(gift, hydrated),
      url: getGiftUrl(gift, hydrated, variantId),
      image: getGiftImage(gift, hydrated),
      compareAtPrice: getGiftCompareAtPrice(gift, hydrated),
    };

    giftMetaCache.set(variantId, normalized);

    return {
      ...gift,
      ...normalized,
    };
  }

  function preloadGiftMetadataForCurrentProduct(selectedVariantId) {
    getGiftsForCurrentProduct(selectedVariantId).forEach((gift) => {
      hydrateGiftMeta(gift);
    });
  }

  function buildOptimisticGiftLi(gift, quantity = 1) {
    const variantId = Number(gift.variantId || gift.variant_id || gift.id || 0);
    const title = getGiftTitle(gift);
    const image = getGiftImage(gift);
    const compareAtPrice = getGiftCompareAtPrice(gift);
    const giftQuantity = getGiftAddQuantity(quantity);

    const li = document.createElement('li');
    li.className = 'cd-item cd-item--gift is-optimistic';
    li.dataset.key = `optimistic-gift-${variantId}-${Date.now()}`;
    li.dataset.variant = variantId;
    li.dataset.giftItem = 'true';
    li.dataset.optimisticItem = 'true';

    li.innerHTML = `
      <span class="cd-item-thumb" aria-hidden="true" style="pointer-events:none;cursor:default;">
        ${image ? `<img src="${esc(image)}" alt="${esc(title)}" width="80" height="80" loading="eager">` : ''}
      </span>
      <div class="cd-item-info">
        <span class="cd-item-name" style="pointer-events:none;cursor:default;">${esc(title)}</span>
        <span class="cd-badge-free">FREE</span>
        <div class="cd-item-foot">
          <div class="cd-qty-widget cd-qty-widget--gift is-locked">
            <button type="button" class="cd-qbtn" disabled aria-disabled="true" tabindex="-1" aria-label="Gift quantity cannot be decreased">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                <path d="M1 1H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="cd-qty-val">${giftQuantity}</span>
            <button type="button" class="cd-qbtn" disabled aria-disabled="true" tabindex="-1" aria-label="Gift quantity cannot be increased">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="cd-price-block">
            <span class="cd-p cd-p-sale">${moneyHTML(0)}</span>
            ${compareAtPrice ? `<span class="cd-p cd-p-was">${moneyHTML(compareAtPrice * giftQuantity)}</span>` : ''}
          </div>
        </div>
      </div>
      <button type="button" class="cd-del" disabled aria-label="Remove gift">
        <svg width="13" height="15" viewBox="0 0 13 15" fill="none" aria-hidden="true">
          <path d="M1 3.5H12M4.5 3.5V2H8.5V3.5M2 3.5L2.6 13H10.4L11 3.5"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    return li;
  }

  function updateOptimisticGiftLi(li, gift) {
    if (!li || !gift) return;

    const variantId = Number(gift.variantId || gift.variant_id || gift.id || 0);
    const title = getGiftTitle(gift);
    const image = getGiftImage(gift);
    const compareAtPrice = getGiftCompareAtPrice(gift);

    const thumb = qs('.cd-item-thumb', li);
    const name = qs('.cd-item-name', li);
    const priceBlock = qs('.cd-price-block', li);
    const giftQuantity = Math.max(1, Number(qs('.cd-qty-val', li)?.textContent || 1));

    if (name) {
      name.textContent = title;
    }

    if (thumb && image) {
      thumb.innerHTML = `<img src="${esc(image)}" alt="${esc(title)}" width="80" height="80" loading="eager">`;
    }

    if (priceBlock) {
      priceBlock.innerHTML = `
        <span class="cd-p cd-p-sale">${moneyHTML(0)}</span>
        ${compareAtPrice ? `<span class="cd-p cd-p-was">${moneyHTML(compareAtPrice * giftQuantity)}</span>` : ''}
      `;
    }

    if (variantId) {
      li.dataset.variant = variantId;
    }
  }

  async function renderOptimisticGifts(list, gifts, quantity = 1) {
    if (!list || !gifts || !gifts.length) return;

    hideEmptyState();

    const footerEl = document.querySelector('#CartDrawer .cd-footer');
    if (footerEl) footerEl.style.display = '';

    const cachedCart = window._giftSyncCartCache;
    const presentGiftVariants = new Set(
      (cachedCart?.items || [])
        .filter((item) => window.GiftSync?.isGift?.(item))
        .map((item) => Number(item.variant_id))
    );

    // Find the last optimistic main item to insert gifts after it
    const allOptimistic = list.querySelectorAll('[data-optimistic-item="true"]:not([data-gift-item])');
    let insertAfter = allOptimistic.length ? allOptimistic[allOptimistic.length - 1] : null;

    gifts.forEach((rawGift) => {
      const giftVariantId = Number(rawGift.variantId || rawGift.variant_id || rawGift.id || 0);

      if (!giftVariantId) return;
      if (presentGiftVariants.has(giftVariantId)) return;

      // Remove any existing optimistic for this gift variant
      qs(`[data-optimistic-item="true"][data-variant="${giftVariantId}"]`, list)?.remove();

      const cachedMeta = giftMetaCache.get(giftVariantId);
      const initialGift = cachedMeta ? { ...rawGift, ...cachedMeta } : rawGift;
      const giftLi = buildOptimisticGiftLi(initialGift, quantity);

      if (insertAfter) {
        insertAfter.after(giftLi);
        insertAfter = giftLi;
      } else {
        list.prepend(giftLi);
        insertAfter = giftLi;
      }

      // Hydrate asynchronously and update the placeholder
      hydrateGiftMeta(rawGift).then((hydratedGift) => {
        const currentLi = qs(
          `[data-optimistic-item="true"][data-variant="${giftVariantId}"]`,
          list
        );
        updateOptimisticGiftLi(currentLi, hydratedGift);
      });
    });
  }

  // Legacy wrapper used by PDP form submit handler
  async function renderOptimisticGiftsFromForm(form) {
    const list = document.getElementById('cd-items');
    if (!list) return;

    const selectedVariant = getSelectedVariant(form);
    const selectedVariantId = Number(selectedVariant?.id || new FormData(form).get('id') || 0);
    if (!selectedVariantId) return;

    const qty = Math.max(1, Number(qs('[name="quantity"]', form)?.value || 1));
    const rawGifts = getGiftsForCurrentProduct(selectedVariantId);
    await renderOptimisticGifts(list, rawGifts, qty);
  }

  function clearOptimisticItems() {
    document.querySelectorAll('[data-optimistic-item="true"]').forEach((el) => el.remove());
    document.querySelectorAll('.cd-item.is-optimistic').forEach((el) => el.classList.remove('is-optimistic'));
  }

  const CARD_PRODUCT_CACHE = {};

  function parseCollectionPriceToCents(value) {
    const raw = String(value || '').trim();

    if (!raw) return 0;

    if (/^\d+$/.test(raw)) {
      return Number(raw);
    }

    let clean = raw.replace(/[^\d.,-]/g, '');
    const hasComma = clean.includes(',');
    const hasDot = clean.includes('.');

    if (hasComma && hasDot) {
      clean = clean.lastIndexOf(',') > clean.lastIndexOf('.')
        ? clean.replace(/\./g, '').replace(',', '.')
        : clean.replace(/,/g, '');
    } else if (hasComma) {
      clean = clean.replace(/\./g, '').replace(',', '.');
    }

    const number = parseFloat(clean);

    return Number.isNaN(number) ? 0 : Math.round(number * 100);
  }

  function getCollectionRoot(source) {
    if (!source || source === document) return document;

    return (
      source.closest?.('.cw-hp-product') ||
      source.closest?.('[data-product-id]') ||
      source.closest?.('.grid-product') ||
      source.closest?.('.collection-grid-item') ||
      source.closest?.('#CollectionAjaxContent') ||
      source.closest?.('.product-card') ||
      source.closest?.('.card-wrapper') ||
      source.closest?.('.grid__item') ||
      source.closest?.('form') ||
      source.parentElement ||
      document
    );
  }

  function getCardProductId(source) {
    const root = getCollectionRoot(source);

    return Number(
      source?.dataset?.productId ||
      source?.dataset?.productid ||
      source?.dataset?.product ||
      root?.dataset?.productId ||
      root?.dataset?.productid ||
      root?.dataset?.product ||
      source?.querySelector?.('[data-product-id], [data-productid]')?.dataset?.productId ||
      source?.querySelector?.('[data-product-id], [data-productid]')?.dataset?.productid ||
      root?.querySelector?.('[data-product-id], [data-productid]')?.dataset?.productId ||
      root?.querySelector?.('[data-product-id], [data-productid]')?.dataset?.productid ||
      source?.querySelector?.('[name="product-id"], [name="product_id"]')?.value ||
      root?.querySelector?.('[name="product-id"], [name="product_id"]')?.value ||
      0
    );
  }

  function getCardProductHandle(source) {
    const root = getCollectionRoot(source);
    const href =
      source?.dataset?.productUrl ||
      source?.dataset?.url ||
      root?.dataset?.productUrl ||
      root?.dataset?.url ||
      root?.querySelector?.('a[href*="/products/"]')?.getAttribute('href') ||
      '';

    const fromHref = href.includes('/products/')
      ? href.split('/products/')[1].split('?')[0].split('#')[0].replace(/\/$/, '')
      : '';

    return String(
      source?.dataset?.productHandle ||
      root?.dataset?.productHandle ||
      fromHref ||
      ''
    ).trim();
  }

  async function getCardProductData(source) {
    const productId = getCardProductId(source);
    const handle = getCardProductHandle(source);

    if (productId) {
      return {
        id: productId,
        handle,
        product: null,
      };
    }

    if (!handle) {
      return {
        id: 0,
        handle: '',
        product: null,
      };
    }

    if (CARD_PRODUCT_CACHE[handle]) {
      return CARD_PRODUCT_CACHE[handle];
    }

    try {
      const res = await fetch(`/products/${handle}.js`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!res.ok) {
        return {
          id: 0,
          handle,
          product: null,
        };
      }

      const product = await res.json();

      CARD_PRODUCT_CACHE[handle] = {
        id: Number(product.id || 0),
        handle,
        product,
      };

      return CARD_PRODUCT_CACHE[handle];
    } catch (_) {
      return {
        id: 0,
        handle,
        product: null,
      };
    }
  }

  function getUniversalCardMeta(source, variantId) {
    const root = getCollectionRoot(source);

    const title =
      source?.dataset?.productTitle ||
      source?.dataset?.title ||
      root?.dataset?.productTitle ||
      root?.dataset?.title ||
      root?.querySelector?.('.cw-hp-product-tittle, .grid-product__title, .product-card__title, .card__heading, .cw-product-card__title, [data-product-title]')?.textContent?.trim() ||
      'Product';

    const url =
      source?.dataset?.productUrl ||
      source?.dataset?.url ||
      root?.dataset?.productUrl ||
      root?.dataset?.url ||
      root?.querySelector?.('a[href*="/products/"]')?.getAttribute('href') ||
      '#';

    const image =
      source?.dataset?.image ||
      source?.dataset?.imageUrl ||
      root?.dataset?.image ||
      root?.dataset?.imageUrl ||
      root?.querySelector?.('img')?.currentSrc ||
      root?.querySelector?.('img')?.src ||
      root?.querySelector?.('img')?.getAttribute('data-src') ||
      '';

    const priceCents = parseCollectionPriceToCents(
      source?.dataset?.price ||
      source?.dataset?.variantPrice ||
      source?.dataset?.priceCents ||
      root?.dataset?.price ||
      root?.dataset?.variantPrice ||
      root?.querySelector?.('.cw-hp-price .money, .grid-product__price .money, .product-card__price .money, .price .money, [data-product-price]')?.textContent ||
      ''
    );

    const compareAtCents = parseCollectionPriceToCents(
      source?.dataset?.compareAtPrice ||
      source?.dataset?.comparePrice ||
      root?.dataset?.compareAtPrice ||
      root?.dataset?.comparePrice ||
      root?.querySelector?.('.cw-hp-compare-price .money, .grid-product__price--original .money, .product-card__compare-price .money, .price__compare .money, s .money, [data-compare-price]')?.textContent ||
      ''
    );

    return {
      variantId: Number(variantId || 0),
      productId: getCardProductId(source),
      title,
      url,
      image,
      priceCents,
      compareAtCents,
    };
  }

  function buildUniversalOptimisticMainLi(meta, quantity = 1) {
    const li = document.createElement('li');
    const qty = Math.max(1, Number(quantity || 1));

    li.className = 'cd-item is-optimistic';
    li.dataset.key = `optimistic-card-${meta.variantId}-${Date.now()}`;
    li.dataset.variant = String(meta.variantId);
    li.dataset.optimisticItem = 'true';

    const linePrice = Number(meta.priceCents || 0) * qty;
    const compareLine = Number(meta.compareAtCents || 0) * qty;
    const priceHtml = meta.priceCents
      ? (
        compareLine > linePrice
          ? `<span class="cd-p cd-p-sale">${moneyHTML(linePrice)}</span><span class="cd-p cd-p-was">${moneyHTML(compareLine)}</span>`
          : `<span class="cd-p">${moneyHTML(linePrice)}</span>`
      )
      : `<span class="cd-p">Adding...</span>`;

    li.innerHTML = `
      <a href="${esc(meta.url || '#')}" class="cd-item-thumb" tabindex="-1" aria-hidden="true">
        ${meta.image ? `<img src="${esc(meta.image)}" alt="${esc(meta.title)}" width="80" height="80" loading="eager">` : ''}
      </a>

      <div class="cd-item-info">
        <a href="${esc(meta.url || '#')}" class="cd-item-name">${esc(meta.title)}</a>

        <div class="cd-item-foot">
          <div class="cd-qty-widget">
            <button type="button" class="cd-qbtn" disabled aria-label="Decrease">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                <path d="M1 1H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>

            <span class="cd-qty-val">${qty}</span>

            <button type="button" class="cd-qbtn" disabled aria-label="Increase">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <div class="cd-price-block">${priceHtml}</div>
        </div>
      </div>

      <button type="button" class="cd-del" disabled aria-label="Remove">
        <svg width="13" height="15" viewBox="0 0 13 15" fill="none" aria-hidden="true">
          <path d="M1 3.5H12M4.5 3.5V2H8.5V3.5M2 3.5L2.6 13H10.4L11 3.5"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    return li;
  }

  function updateOptimisticSubtotalFromCard(meta, quantity = 1) {
    if (!meta.priceCents) return;

    const qty = Math.max(1, Number(quantity || 1));

    const subtotalEl = qs('[data-subtotal-price]');
    const labelEl = qs('[data-subtotal-label]');

    if (subtotalEl) {
      setMoneyHTML(subtotalEl, getCurrentSubtotalCents() + (meta.priceCents * qty));
    }

    if (labelEl) {
      const match = labelEl.textContent.match(/\((\d+)/);
      const current = match ? Number(match[1]) : document.querySelectorAll('#cd-items .cd-item').length;
      const next = current + qty;

      labelEl.textContent = `Subtotal (${next} item${next === 1 ? '' : 's'})`;
    }
  }

  function updateUniversalProgressFromCard(source) {
    const fill = qs('[data-progress-fill]');
    const label = qs('[data-progress-label]');

    if (!fill || !label) return;

    const sellingPlan =
      source?.dataset?.sellingPlanId ||
      source?.querySelector?.('[name="selling_plan"]')?.value ||
      '';

    if (!sellingPlan) return;

    fill.style.width = '100%';
    fill.classList.add('is-full');
    label.innerHTML = "🎉 Congrats! You've unlocked <strong>FREE SHIPPING!</strong>";
  }

  function renderUniversalCardMainOptimistic(source, variantId, quantity = 1) {
    const list = document.getElementById('cd-items');
    if (!list || !variantId) return;

    const qty = Math.max(1, Number(quantity || 1));
    const meta = getUniversalCardMeta(source, variantId);

    hideEmptyState();

    const footerEl = document.querySelector('#CartDrawer .cd-footer');
    if (footerEl) footerEl.style.display = '';

    const existingReal = qs(`.cd-item[data-variant="${variantId}"]:not([data-optimistic-item])`, list);

    if (existingReal) {
      existingReal.classList.add('is-optimistic');

      const qtyEl = qs('.cd-qty-val', existingReal);
      const priceBlock = qs('.cd-price-block', existingReal);
      const nextQty = Number(qtyEl?.textContent || 1) + qty;

      if (qtyEl) qtyEl.textContent = nextQty;

      if (priceBlock && meta.priceCents) {
        const linePrice = meta.priceCents * nextQty;
        const comparePrice = meta.compareAtCents * nextQty;

        priceBlock.innerHTML = comparePrice > linePrice
          ? `<span class="cd-p cd-p-sale">${moneyHTML(linePrice)}</span><span class="cd-p cd-p-was">${moneyHTML(comparePrice)}</span>`
          : `<span class="cd-p">${moneyHTML(linePrice)}</span>`;
      }
    } else {
      qs(`[data-optimistic-item="true"][data-variant="${variantId}"]`, list)?.remove();
      list.prepend(buildUniversalOptimisticMainLi(meta, qty));
    }

    updateOptimisticSubtotalFromCard(meta, qty);
    updateUniversalProgressFromCard(source);
  }

  function getCollectionVariantIdFromForm(form) {
    const formData = new FormData(form);

    return Number(
      formData.get('id') ||
      qs('[name="id"]', form)?.value ||
      form.dataset?.variantId ||
      qs('[data-variant-id]', form)?.getAttribute('data-variant-id') ||
      0
    );
  }

  function getCollectionQuantityFromForm(form) {
    const formData = new FormData(form);
    return Math.max(1, Number(formData.get('quantity') || qs('[name="quantity"]', form)?.value || 1));
  }

  async function buildUniversalCardPayload(source, variantId, sellingPlanId, quantity) {
    const productData = await getCardProductData(source);
    const productId = Number(productData.id || 0);
    const mainQuantity = Math.max(1, Number(quantity || 1));
    const giftQuantity = getGiftAddQuantity(mainQuantity);
    const mainItem = {
      id: Number(variantId),
      quantity: mainQuantity,
    };

    if (sellingPlanId) {
      mainItem.selling_plan = sellingPlanId;
    }

    if (source?.tagName === 'FORM') {
      const properties = getLineItemPropertiesFromForm(new FormData(source));

      if (Object.keys(properties).length) {
        mainItem.properties = properties;
      }
    }

    const gifts = productId ? getGiftsForVariant(variantId, productId, source) : [];
    const runtimeRule = buildRuntimeGiftRule(source, variantId, productId, gifts);

    if (runtimeRule) {
      registerDynamicGiftRule(runtimeRule);
    }

    const giftItems = gifts
      .map((gift) => ({
        id: Number(gift.variantId || gift.variant_id || gift.id || 0),
        quantity: giftQuantity,
        properties: {
          _gift: 'true',
          _parent_product_id: String(productId),
          _parent_variant_id: String(variantId),
        },
      }))
      .filter((item) => item.id && item.id !== Number(variantId));

    return {
      payload: {
        items: [mainItem, ...giftItems],
      },
      gifts,
    };
  }

  /* ─────────────────────────────────────────────────────────────
     8.  ATC form submission handler
         - PDP: optimized PDP flow
         - Collection forms: same card flow as home page
  ───────────────────────────────────────────────────────────── */

  function isCollectionContext(form) {
    return !!form.closest('.grid-product, .collection-grid-item, #CollectionAjaxContent, .cw-hp-product, .product-card, .card-wrapper, .grid__item');
  }

  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!form.classList.contains('product-single__form')) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      suppressRebuyCart(4500);

      const submitBtn = qs('[data-add-to-cart]', form);
      const btnTextEl = qs('[data-add-to-cart-text]', form);
      const defaultTxt = btnTextEl?.getAttribute('data-default-text') || btnTextEl?.textContent || '';
      const loadingTxt = btnTextEl?.getAttribute('data-loading-text') || 'Adding...';

      if (submitBtn) submitBtn.disabled = true;
      if (btnTextEl) btnTextEl.textContent = loadingTxt;

      const isCollection = isCollectionContext(form);
      let optimisticTimer = null;
      let payload = null;
      let collectionGifts = [];
      let collectionVariantId = 0;
      let collectionQuantity = 1;

      try {
        if (isCollection) {
          const formData = new FormData(form);
          collectionVariantId = getCollectionVariantIdFromForm(form);
          const sellingPlan = String(formData.get('selling_plan') || qs('[name="selling_plan"]', form)?.value || '').trim();
          const qty = getCollectionQuantityFromForm(form);
          collectionQuantity = qty;

          if (!collectionVariantId) {
            throw new Error('Missing selected variant ID.');
          }

          const built = await buildUniversalCardPayload(form, collectionVariantId, sellingPlan, qty);

          payload = built.payload;
          collectionGifts = built.gifts;
        } else {
          const forcedVariantId = forceFormVariantFromSwatcher(form);
          const selectedVariant = getSelectedVariant(form);
          const selectedVariantId = forcedVariantId || selectedVariant?.id || new FormData(form).get('id');

          registerDynamicGiftRule(getVariantGiftRule(selectedVariantId));
          payload = buildCartAddPayloadFromForm(form);
        }
      } catch (err) {
        console.error('[ATC] Payload error:', err);

        if (submitBtn) submitBtn.disabled = false;
        if (btnTextEl) btnTextEl.textContent = defaultTxt;

        if (!window.__ksCheckoutRedirecting) (window.ksToast || window.alert)(err?.message || 'Could not add item to cart.'); // no pop-up while heading to checkout
        return;
      }

      if (isCollection) {
        const list = document.getElementById('cd-items');

        renderUniversalCardMainOptimistic(form, collectionVariantId, collectionQuantity);

        if (collectionGifts.length && list) {
          renderOptimisticGifts(list, collectionGifts, collectionQuantity);
        }

        openDrawerNow();
        suppressRebuyCart(4500);
      } else {
        optimisticTimer = setTimeout(() => {
          renderOptimisticItem(form);
          renderOptimisticGiftsFromForm(form);
          suppressRebuyCart(4500);
        }, OPTIMISTIC_DELAY);
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((res) => {
          if (!res.ok) return res.json().then((err) => Promise.reject(err));
          return res.json();
        })
        .then(() => fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }))
        .then((res) => res.json())
        .then((cart) => {
          clearTimeout(optimisticTimer);
          suppressRebuyCart(4500);

          if (!isCollection) {
            openDrawerNow();
          }

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart },
            })
          );

          clearOptimisticItems();
        })
        .catch((err) => {
          clearTimeout(optimisticTimer);
          clearOptimisticItems();

          console.error('[ATC] Error:', err);

          const msg = err?.description || err?.message || 'Could not add item to cart.';
          if (!window.__ksCheckoutRedirecting) (window.ksToast || window.alert)(msg); // no pop-up while heading to checkout
        })
        .finally(() => {
          if (submitBtn) submitBtn.disabled = false;
          if (btnTextEl) btnTextEl.textContent = defaultTxt;
        });
    },
    true
  );

  /* ─────────────────────────────────────────────────────────────
     9.  Home page / card ATC
         Uses main item + gifts in one /cart/add.js payload
  ───────────────────────────────────────────────────────────── */

  document.addEventListener(
    'click',
    async (e) => {
      const btn = e.target.closest('.cw-hp-product-atc-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const variantId = Number(btn.dataset.variantId || 0);
      const sellingPlan = String(btn.dataset.sellingPlanId || '').trim();

      if (!variantId) return;

      suppressRebuyCart(4500);
      renderUniversalCardMainOptimistic(btn, variantId, 1);
      openDrawerNow();

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Adding...';

      try {
        const list = document.getElementById('cd-items');
        const built = await buildUniversalCardPayload(btn, variantId, sellingPlan, 1);
        const payload = built.payload;
        const gifts = built.gifts;

        if (gifts.length && list) {
          renderOptimisticGifts(list, gifts, 1);
        }

        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!addRes.ok) {
          const err = await addRes.json();
          throw err;
        }

        const cartRes = await fetch('/cart.js', {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        const cart = await cartRes.json();

        suppressRebuyCart(4500);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart },
          })
        );

        clearOptimisticItems();
      } catch (err) {
        clearOptimisticItems();

        console.error('[ATC Card] Error:', err);

        const msg = err?.description || err?.message || 'Could not add item to cart.';
        if (!window.__ksCheckoutRedirecting) (window.ksToast || window.alert)(msg); // no pop-up while heading to checkout
      } finally {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    },
    true
  );
})();
}

/* REY - Straight to Checkout script */
(function () {
  'use strict';

  if (window.__cwCheckoutRedirectInit) return;
  window.__cwCheckoutRedirectInit = true;

  /* ── Edit these ── */
  var CHECKOUT_VARIANT_IDS = [47000190386396, 47000190419164, 47000190451932];
  var CHECKOUT_PRODUCT_IDS = [];
  var CHECKOUT_URL         = '/checkout';

  var vSet = new Set(CHECKOUT_VARIANT_IDS.map(Number));
  var pSet = new Set(CHECKOUT_PRODUCT_IDS.map(Number));
  if ((!vSet.size && !pSet.size) || !window.fetch) return;

  var redirecting = false;
  var armed = false;   // this add is checkout-bound; suppress drawer + redirect on resolve

  function itemMatches(it) {
    if (!it) return false;
    return vSet.has(Number(it.id || it.variant_id)) || pSet.has(Number(it.product_id));
  }

  function payloadMatches(body) {
    if (!body) return false;
    var data;
    try { data = typeof body === 'string' ? JSON.parse(body) : body; } catch (_) { return false; }
    if (Array.isArray(data.items)) return data.items.some(itemMatches);
    return itemMatches(data);
  }

  function responseMatches(data) {
    if (!data) return false;
    if (Array.isArray(data.items)) return data.items.some(itemMatches);
    return itemMatches(data);
  }

  // Hard-hide the drawer so the existing openDrawerNow() timer can never show it.
  function hideDrawerHard() {
    if (!document.getElementById('cw-checkout-hide-drawer')) {
      var s = document.createElement('style');
      s.id = 'cw-checkout-hide-drawer';
      s.textContent =
        '#CartDrawer,#cd-overlay{display:none!important;visibility:hidden!important;opacity:0!important;}';
      document.head.appendChild(s);
    }
    document.documentElement.classList.remove('js-drawer-open', 'js-drawer-open--right');
    document.body.style.overflow = '';
  }

  function goToCheckout() {
    if (redirecting) return;
    redirecting = true;
    hideDrawerHard();
    window.location.replace(CHECKOUT_URL);
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = '';
    try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (_) {}

    var isAdd  = /\/cart\/add(\.js)?(\?|$)/.test(url);
    var isCart = /\/cart(\.js)?(\?|$)/.test(url) && !isAdd;

    // 1. On the ADD request: inspect the body up front. If it's checkout-bound,
    //    arm immediately and hide the drawer NOW — before the existing handler's
    //    openDrawerNow() timer can fire.
    if (isAdd) {
      var body = init && init.body;
      if (!body && input && typeof input !== 'string') { try { body = input.body; } catch (_) {} }
      if (payloadMatches(body)) {
        armed = true;
        hideDrawerHard();
      }
    }

    // 2. If armed, short-circuit the follow-up /cart.js refetch — we don't need it,
    //    and skipping it removes a whole round trip before navigation.
    if (armed && isCart) {
      goToCheckout();
      // Return a never-resolving promise so the existing .then chain stalls
      // harmlessly while the browser navigates away.
      return new Promise(function () {});
    }

    var promise = origFetch.apply(this, arguments);

    // 3. Redirect the instant the ADD resolves. If we were already armed from the
    //    payload, this is just the confirmation; either way we leave here.
    if (isAdd) {
      promise.then(function (res) {
        if (!res || !res.ok || redirecting) return;
        res.clone().json()
          .then(function (data) { if (armed || responseMatches(data)) goToCheckout(); })
          .catch(function () { if (armed) goToCheckout(); });
      }).catch(function () {});
    }

    return promise;
  };
})();